import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ActionButton } from '@/components/design-system';
import { useFormations, useFormateurs } from '@/hooks/queries/useFormation';
import { useUpsertB2cAction, type B2cFormateurPaiementType } from '@/hooks/queries/useB2c';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved?: () => void;
}

export function B2cActionFormDialog({ open, onOpenChange, onSaved }: Props): JSX.Element {
  const navigate = useNavigate();
  const upsert = useUpsertB2cAction();
  const { data: formations = [] } = useFormations({});
  const { data: formateurs = [] } = useFormateurs();

  const [formationId, setFormationId] = useState('');
  const [formateurId, setFormateurId] = useState('');
  const [dateAction, setDateAction] = useState('');
  const [horaire, setHoraire] = useState('');
  const [lieu, setLieu] = useState('');
  const [capaciteMax, setCapaciteMax] = useState('12');
  const [prixUnitaire, setPrixUnitaire] = useState('');
  const [paiementType, setPaiementType] = useState<B2cFormateurPaiementType>('forfait_fixe');
  const [forfait, setForfait] = useState('');
  const [pctCa, setPctCa] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setFormationId('');
      setFormateurId('');
      setDateAction('');
      setHoraire('');
      setLieu('');
      setCapaciteMax('12');
      setPrixUnitaire('');
      setPaiementType('forfait_fixe');
      setForfait('');
      setPctCa('');
      setError(null);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (!dateAction || !prixUnitaire) {
      setError('Date et prix obligatoires');
      return;
    }
    try {
      const action = await upsert.mutateAsync({
        formation_id: formationId || null,
        formateur_id: formateurId || null,
        date_action: dateAction,
        horaire: horaire || null,
        lieu: lieu || null,
        capacite_max: Number(capaciteMax) || 12,
        prix_unitaire_b2c: Number(prixUnitaire),
        formateur_paiement_type: paiementType,
        formateur_montant_forfait:
          paiementType === 'forfait_fixe' && forfait ? Number(forfait) : null,
        formateur_pct_ca: paiementType === 'pourcentage_ca' && pctCa ? Number(pctCa) : null,
      });
      toast.success(`Action ${action.reference} créée`);
      onSaved?.();
      navigate(`/formation-b2c/${action.id}`);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Nouvelle action B2C</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            {error && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                {error}
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Formation">
                <select
                  value={formationId}
                  onChange={(e) => setFormationId(e.target.value)}
                  className="input"
                >
                  <option value="">— Catalogue —</option>
                  {formations.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.code} · {f.titre}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Formateur">
                <select
                  value={formateurId}
                  onChange={(e) => setFormateurId(e.target.value)}
                  className="input"
                >
                  <option value="">— Aucun —</option>
                  {formateurs.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.prenom} {f.nom}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Date" required>
                <input
                  type="date"
                  value={dateAction}
                  onChange={(e) => setDateAction(e.target.value)}
                  className="input"
                  required
                />
              </Field>
              <Field label="Horaire">
                <input
                  value={horaire}
                  onChange={(e) => setHoraire(e.target.value)}
                  placeholder="Samedi 14h-17h"
                  className="input"
                />
              </Field>
            </div>
            <Field label="Lieu">
              <input
                value={lieu}
                onChange={(e) => setLieu(e.target.value)}
                className="input"
                placeholder="Atelier Bab Ezzouar"
              />
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Capacité max">
                <input
                  type="number"
                  value={capaciteMax}
                  onChange={(e) => setCapaciteMax(e.target.value)}
                  className="input"
                />
              </Field>
              <Field label="Prix unitaire B2C (DA)" required>
                <input
                  type="number"
                  value={prixUnitaire}
                  onChange={(e) => setPrixUnitaire(e.target.value)}
                  className="input"
                  required
                />
              </Field>
            </div>
            <Field label="Modèle paiement formateur">
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setPaiementType('forfait_fixe')}
                  className={
                    'rounded-xl border-2 px-3 py-2 text-xs font-bold transition-all ' +
                    (paiementType === 'forfait_fixe'
                      ? 'border-rose-500 bg-rose-50 text-rose-700'
                      : 'border-slate-200 bg-white text-slate-600')
                  }
                >
                  Forfait fixe (DA)
                </button>
                <button
                  type="button"
                  onClick={() => setPaiementType('pourcentage_ca')}
                  className={
                    'rounded-xl border-2 px-3 py-2 text-xs font-bold transition-all ' +
                    (paiementType === 'pourcentage_ca'
                      ? 'border-rose-500 bg-rose-50 text-rose-700'
                      : 'border-slate-200 bg-white text-slate-600')
                  }
                >
                  % du CA
                </button>
              </div>
            </Field>
            {paiementType === 'forfait_fixe' ? (
              <Field label="Forfait formateur (DA)">
                <input
                  type="number"
                  value={forfait}
                  onChange={(e) => setForfait(e.target.value)}
                  className="input"
                />
              </Field>
            ) : (
              <Field label="Pourcentage CA (%)">
                <input
                  type="number"
                  step="0.5"
                  value={pctCa}
                  onChange={(e) => setPctCa(e.target.value)}
                  className="input"
                />
              </Field>
            )}
          </DialogBody>
          <DialogFooter>
            <ActionButton type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Annuler
            </ActionButton>
            <ActionButton
              type="submit"
              variant="gradient"
              module="formation_b2c"
              loading={upsert.isPending}
            >
              Créer l&apos;action
            </ActionButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-slate-700">
        {label}
        {required && <span className="text-rose-500"> *</span>}
      </span>
      {children}
    </label>
  );
}
